import SalesAttributionBarChart from 'components/molecules/SalesAttributionBarChart/SalesAttributionBarChart';
import { colorConstants } from 'config/colorConstants';
import _ from 'lodash';
import { Tooltip } from 'antd';
//import { renderTitle } from './getInternalSummaryTableColumns';
import {
    calculateFontSize,
    fetchCurrency,
    formatNumWithKMBAlt,
    formatString,
    scaleMinValue,
    ellipseMiddleString,
} from './helperMethods';

const disabledAttributeItems: any[] = [];
const defaultTableLabelFontSize = calculateFontSize(window.innerWidth, 16);
export const graphXAxisLabelFontSize = calculateFontSize(window.innerWidth, 13);
//COMMON FUNCTION FOR RENDERING TABLE HEADERS
export const renderTitle = (
    title: string,
    position: string | null = 'center',
    from: 'potential_upside' | 'category_impacted' = 'category_impacted',
    className: string = '',
    fontSize: number = 12,
    characterLimit?: number,
) => {
    return (
        <>
            <div
                title={characterLimit ? title : undefined}
                style={{
                    height: '100%',
                    width: '100%',
                    fontSize: from === 'category_impacted' ? fontSize : defaultTableLabelFontSize,
                    fontWeight: 600,
                    overflowWrap: 'anywhere',
                    padding: `10px 10px 10px ${position ? '10px' : '15px'}`,
                }}
                className={`${position ? 'justify-center' : ''} h-9 flex items-center ${className}`}
            >
                {characterLimit ? formatString(title, characterLimit) : title}
            </div>
        </>
    );
};

export const renderTitleExpandDataHeader = (
    title: string,
    position: string | null = 'center',
    from: 'potential_upside' | 'category_impacted' = 'category_impacted',
    className: string = '',
    fontSize: number = 12,
    characterLimit?: number,
) => {
    return (
        <>
            <div
                style={{
                    height: '100%',
                    width: '100%',
                    fontSize: from === 'category_impacted' ? fontSize : defaultTableLabelFontSize,
                    fontWeight: 600,
                    overflowWrap: 'anywhere',
                    padding: `10px 10px 10px ${position ? '10px' : '15px'}`,
                }}
                className={`${position ? 'justify-center' : ''} h-9 flex items-center ${className}`}
            >
                <Tooltip
                    placement="top"
                    color="white"
                    destroyTooltipOnHide
                    overlayStyle={{ borderRadius: 4 }}
                    overlayInnerStyle={{
                        fontSize: graphXAxisLabelFontSize,
                        color: colorConstants.darkGrey,
                    }}
                    title={title}
                >
                    <div> {ellipseMiddleString(title, 8, 4)}</div>
                </Tooltip>
            </div>
        </>
    );
};

//attribute sales column generator
export const getSalesAttributionColumns = (
    colorConfig: any[][],
    selectedButton: any,
    data: any,
    chartId: string = '',
    extras: any,
): any => {
    const { onClick, selected, attributeTypeIndex, config } = extras;
    const maxB = extras.max;
    const minB = extras.min;
    const currency = selectedButton?.key === 'val' ? fetchCurrency(config) : '';
    const decimalForPercentage = config?.commonConfig?.decimalForPercentage;
    const decimalForNonPercentage = config?.commonConfig?.decimalForNonPercentage;
    const headerTitle = extras.headerTitle;
    const scale = 7;
    const yAxisWidth = Math.max(
        scale * formatNumWithKMBAlt(minB, 2, 2)?.length,
        scale * formatNumWithKMBAlt(maxB, 2, 2)?.length,
        calculateFontSize(window.innerWidth, 90),
    ); //adjust width of y axis according to the length of the y axis values
    //const singleCellWidth = true ? (1570 - 2 - yAxisWidth) / extras?.barLength : 73;
    const singleCellWidth = extras?.width ? (extras?.width - yAxisWidth) / extras?.barLength : 73; //CHANGE LATER

    const handleItemClick = (item: any[]) => {
        if (selected === item[0]?.type) return onClick('');
        onClick(item[0]?.type);
    };
    const generateBgClass = (type: string) => {
        return type === selected && onClick ? 'bg-bgBackground' : '';
    };
    const headerFont = calculateFontSize(window.innerWidth, 11);
    const chartHeight = calculateFontSize(window.innerWidth, 200);
    const columns =
        data &&
        data.length > 0 &&
        Object.keys(data[0]).map((item: any, index: number) => {
            const width = index === 0 ? yAxisWidth : data[1][item].length * singleCellWidth;
            const className = 'm-2 !mb-0 px-2 pt-0 bg-white relative top-[7px]';
            return {
                title: renderTitle(headerTitle[index], undefined, undefined, '!p-0', headerFont),
                //title: index > 1 ? renderTitle(item) : '',
                dataIndex: item,
                key: item,
                fixed: index === 0 ? 'left' : null,
                width: width,
                onCell: (record: any, rowIndex: any) => ({
                    style: {
                        padding: 0,
                        backgroundColor: index === 0 ? 'white' : 'auto',
                    },
                }),
                render(val: any, rowVal: any, rowIndex: number) {
                    if (!val && typeof val !== 'number') {
                        return '';
                    }
                    if (typeof val === 'string') {
                        return {
                            props: {
                                style: {
                                    textAlign: 'center',
                                    padding: 0,
                                },
                            },
                            children: <div className="font-xxs min-h-1 text-center">{val}</div>,
                        };
                    }
                    //FOR GROUP PERCENTAGE
                    if (typeof val === 'number') {
                        const formattedNum = formatNumWithKMBAlt(val, decimalForPercentage);
                        return {
                            props: {
                                style: {
                                    textAlign: 'center',
                                    padding: 0,
                                    color: val < 0 ? colorConstants.red : colorConstants.limeGreen,
                                },
                            },
                            children: (
                                <div className="font-xxs min-h-1 text-center">
                                    {isFinite(val) && index === Object.keys(data[0]).length - 1
                                        ? val > 0
                                            ? '▲'
                                            : '▼'
                                        : ''}
                                    {isFinite(val) ? formattedNum + '%' : ''}
                                </div>
                            ),
                        };
                    }
                    //for last item in top array - overall
                    else if (
                        Array.isArray(val) &&
                        val.length > 0 &&
                        typeof val[0] === 'number' &&
                        rowIndex !== 1 &&
                        item === 'overall'
                    ) {
                        return {
                            props: {
                                style: { padding: 0 },
                            },
                            children: (
                                <div style={{ width: singleCellWidth }} className="flex justify-center gap-2">
                                    {val.map((num, index) => {
                                        const formattedNum = formatNumWithKMBAlt(
                                            num,
                                            index === 0 ? decimalForPercentage : decimalForNonPercentage,
                                        );

                                        return (
                                            <div
                                                key={index}
                                                style={{
                                                    fontSize: headerFont,
                                                    color: num < 0 ? colorConstants.red : colorConstants.limeGreen,
                                                }}
                                            >
                                                {index === 1 ? formattedNum : isFinite(num) ? formattedNum + '%' : ''}
                                            </div>
                                        );
                                    })}
                                </div>
                            ),
                        };
                    }
                    //FOR GROUP ITEM PERCENTAGE
                    else if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'number') {
                        return {
                            props: {
                                style: { padding: 0 },
                            },
                            children: (
                                <div className="flex">
                                    {val.map((num, index) => {
                                        const cls = generateBgClass(data[1][item][index]['type']); //changing bg if user clicks on item
                                        const formattedNum = formatNumWithKMBAlt(num, decimalForPercentage);
                                        return (
                                            <div
                                                className={cls}
                                                key={index}
                                                style={{
                                                    fontSize: headerFont,
                                                    color: num < 0 ? colorConstants.red : colorConstants.limeGreen,
                                                    width: singleCellWidth,
                                                    textAlign: 'center',
                                                    borderLeft:
                                                        index === 0 ? 'none' : `1px solid ${colorConstants.borderGrey}`,
                                                }}
                                            >
                                                {isFinite(num) ? formattedNum + '%' : ''}
                                            </div>
                                        );
                                    })}
                                </div>
                            ),
                        };
                        //FOR GRAPH X AXIS LABELS
                    } else if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'string') {
                        return {
                            props: {
                                style: { padding: 0, verticalAlign: 'initial' },
                            },
                            children: (
                                <div className="flex h-full items-stretch">
                                    {val.map((num, index) => {
                                        const cls = generateBgClass(data[1][item][index]['type']); //changing bg if user clicks on item
                                        const width = 100 / val.length || 0;
                                        return (
                                            <>
                                                <div
                                                    className={` p-1 relative z-[2]`}
                                                    key={index}
                                                    style={{
                                                        overflowWrap: 'anywhere',
                                                        color: colorConstants.fontGrey,
                                                        fontSize: headerFont,
                                                        // color: num < 0 ? colorConstants.red : colorConstants.limeGreen,
                                                        width: singleCellWidth,
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {num}
                                                </div>
                                                <div
                                                    style={{
                                                        borderLeft:
                                                            index === 0
                                                                ? 'none'
                                                                : `1px solid ${colorConstants.borderGrey}`,
                                                        width: `${width}%`,
                                                        left: `${index * width}%`,
                                                    }}
                                                    className={`absolute top-0 bottom-0 ${cls}`}
                                                ></div>
                                            </>
                                        );
                                    })}
                                </div>
                            ),
                        };
                        //FOR CHART
                    } else {
                        return {
                            props: {
                                style: {
                                    paddingInline: 0,
                                },
                            },
                            children: (
                                <>
                                    {index === 0 && (
                                        <div className="font-xxs text-center relative top-[-5px] font-semibold whitespace-nowrap ">
                                            {selectedButton?.label} {selectedButton?.label === 'Profit' ? '$' : ''}
                                        </div>
                                    )}
                                    <div className={index === 0 ? className : 'flex'}>
                                        {val?.length > 1 ? (
                                            val.map((prop: any, idx: number) => {
                                                const cls = generateBgClass(prop.type);
                                                return (
                                                    <div
                                                        className={`${cls} ${
                                                            index > 1 &&
                                                            index < Object.keys(data[0]).length - 1 &&
                                                            onClick &&
                                                            !disabledAttributeItems.includes(prop?.type?.toLowerCase())
                                                                ? 'cursor-pointer'
                                                                : ''
                                                        }`}
                                                        onClick={() => {
                                                            if (
                                                                index > 1 &&
                                                                index < Object.keys(data[0]).length - 1 &&
                                                                onClick &&
                                                                !disabledAttributeItems.includes(
                                                                    prop?.type?.toLowerCase(),
                                                                )
                                                            )
                                                                //avoid y axis, 1st item and last item
                                                                handleItemClick([prop]);
                                                        }}
                                                    >
                                                        <SalesAttributionBarChart
                                                            preIntervalLabel={currency}
                                                            dataset={[prop]}
                                                            chartContainer={`${
                                                                prop.type + idx
                                                            }-${attributeTypeIndex}-${chartId}`}
                                                            positiveNegativeConfig={true}
                                                            width={singleCellWidth}
                                                            barWidth="40"
                                                            height={chartHeight}
                                                            minBound={scaleMinValue(40, minB)}
                                                            maxBound={maxB}
                                                            colorList={colorConfig[0]}
                                                            hover={false}
                                                            enableOnClick={
                                                                index > 1 &&
                                                                index < Object.keys(data[0]).length - 1 &&
                                                                onClick &&
                                                                !disabledAttributeItems.includes(
                                                                    prop?.type?.toLowerCase(),
                                                                )
                                                            }
                                                            YaxisConfig={index === 0}
                                                        />
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div
                                                className={`${generateBgClass(val[0]?.type)} ${
                                                    index > 1 &&
                                                    index < Object.keys(data[0]).length - 1 &&
                                                    onClick &&
                                                    !disabledAttributeItems.includes(val?.[0]?.type?.toLowerCase())
                                                        ? 'cursor-pointer'
                                                        : ''
                                                }`}
                                                onClick={() => {
                                                    if (
                                                        index > 1 &&
                                                        index < Object.keys(data[0]).length - 1 &&
                                                        onClick &&
                                                        !disabledAttributeItems.includes(val?.[0]?.type?.toLowerCase())
                                                    )
                                                        //avoid y axis, 1st item and last item
                                                        handleItemClick(val);
                                                }}
                                            >
                                                <SalesAttributionBarChart
                                                    preIntervalLabel={currency}
                                                    dataset={val}
                                                    chartContainer={`${item}-${attributeTypeIndex}-${chartId}`}
                                                    positiveNegativeConfig={true}
                                                    width={width}
                                                    barWidth="40"
                                                    height={index === 0 ? chartHeight - 40 : chartHeight}
                                                    minBound={scaleMinValue(40, minB)}
                                                    maxBound={maxB}
                                                    colorList={
                                                        index === Object.keys(data[0]).length - 1 || index === 1 //for lag 0 and lag 1 values show constant colors
                                                            ? colorConfig[1]
                                                            : colorConfig[0] //show green for positive and red for negative
                                                    }
                                                    hover={false}
                                                    YaxisConfig={index === 0}
                                                    enableOnClick={
                                                        index > 1 &&
                                                        index < Object.keys(data[0]).length - 1 &&
                                                        onClick &&
                                                        !disabledAttributeItems.includes(val?.[0]?.type?.toLowerCase())
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </>
                            ),
                        };
                    }
                },
            };
        });
    return columns || [];
};
